<script>
    // Packages
    import {useForm} from '@inertiajs/vue3';
    import {ref, computed} from 'vue';
    import {Dialog, DialogOverlay, DialogTitle, TransitionChild, TransitionRoot} from '@headlessui/vue';

    // Icons
    import {XIcon} from '@heroicons/vue/outline'
    import {LinkIcon, PlusSmIcon, QuestionMarkCircleIcon} from '@heroicons/vue/solid';

    // Components
    import SlideOutErrors from "@/Components/SlideOutErrors";
    import SlideoutFlashMessage from "@/Components/SlideoutFlashMessage";


    export default {
        components: {
            XIcon,
            Dialog,
            LinkIcon,
            PlusSmIcon,
            DialogTitle,
            DialogOverlay,
            SlideOutErrors,
            TransitionRoot,
            TransitionChild,
            SlideoutFlashMessage,
            QuestionMarkCircleIcon
        },
        setup(prop, context) {

            const trading_details = ref(null);
            
            const currencies = ref(null);

            const bank_details = ref(null);

            const loading = ref(null);
            
            const creating = ref(null);

            const show = ref(false)

            const showErrors = ref(false)

            const form = useForm({
                id: null,
                currency: null,
                bank_details: null
            })

            function submit() {
                this.loading = true;
                this.creating = true;
                this.form.post(route('admin.settings.trading-details.trading-detail.add-bank-account', this.trading_details.id), {
                    onSuccess: (page) => {
                        this.form.reset();
                        this.loading = false;
                        this.creating = false;
                    },
                    onError: (page) => {
                        this.loading = false;
                        this.creating = false;
                        this.showErrors = true;
                    }
                });
            }

            function open() {
                this.form.clearErrors();
                this.show = true;
            }

            const enhancedBankDetails = computed(() => {
                return bank_details.value.map(detail => ({
                    ...detail,
                    label: `${detail.account_name} (${detail.sort_code} - ${detail.account_number})`
                }));
            });

            return {
                show,
                form,
                open,
                submit,
                loading,
                creating,
                currencies,
                showErrors,
                bank_details,
                trading_details,
                enhancedBankDetails
            }
        },
        methods: {
            deleteBankAccount(bank_account) {
                this.$swal.fire({
                    title: 'Removing Bank Details',
                    html: "You are removing bank details. <br/>Are you sure you'd like to do this?",
                    showCloseButton: true,
                    showCancelButton: true,
                    confirmButtonText: 'Yes',
                    cancelButtonText: 'No',
                    stopKeydownPropagation: true,
                    keydownListenerCapture: true
                }).then((result) => {
                    if (result.isConfirmed) {
                        // Trigger the loader
                        this.loading = true;
                        this.creating = true;
                        // Delete the bank account
                        this.$inertia.post(route('admin.settings.trading-details.trading-detail.delete-bank-account', this.trading_details.id), {
                            preserveScroll: true,
                            bank_account_id: bank_account.id,
                            onSuccess: (page) => {
                                this.loading = false,
                                this.creating = false
                            },
                            onError: (page) => {
                                this.loading = false,
                                this.showErrors = true
                            }
                        });
                    }
                });
            },
            close() {
                this.show = false;
                this.form.reset();
                this.form.clearErrors();
                this.$refs.create_vat_rates_slideout_flash.show = false;
                this.$page.props.slideout_flash.success = null;
                this.$page.props.slideout_flash.error = null;
            }
        }
    }
</script>

<template>
    <TransitionRoot as="template" :show="show">
        <Dialog as="div" class="fixed inset-0 overflow-hidden">
            <div class="absolute inset-0 overflow-hidden">
                <DialogOverlay class="absolute inset-0 bg-gray-500 opacity-75" @click="close()"/>
                <div class="fixed inset-y-0 pl-16 max-w-full right-0 flex">
                    <TransitionChild as="template" enter="transform transition ease-in-out duration-500 sm:duration-700"
                        enter-from="translate-x-full" enter-to="translate-x-0"
                        leave="transform transition ease-in-out duration-500 sm:duration-700"
                        leave-from="translate-x-0" leave-to="translate-x-full">
                        <div class="w-screen max-w-2xl">
                            <form class="h-full divide-y divide-gray-200 flex flex-col bg-white shadow-xl" @submit.prevent="submit">
                                <div class="flex-1 h-0 overflow-y-auto">
                                    <div class="py-6 px-4  bg-gradient-to-b from-pitch-purple to-dark-pitch-purple sm:px-6">
                                        <div class="flex items-center justify-between">
                                            <DialogTitle class="text-lg text-white">
                                                Manage Bank Accounts for {{ this.trading_details.trading_name }} {{ this.trading_details.id }} 
                                            </DialogTitle>
                                            <div class="ml-3 h-7 flex items-center">
                                                <button type="button" @click="close()"
                                                    class="bg-dark-pitch-purple rounded-md text-indigo-200 hover:text-white focus:outline-none focus:ring-2 focus:ring-white">
                                                    <span class="sr-only">Close panel</span>
                                                    <XIcon class="h-6 w-6" aria-hidden="true"/>
                                                </button>
                                            </div>
                                        </div>
                                        <div class="mt-1">
                                            <p class="text-sm text-indigo-300">
                                                Get started by filling in the information below to add bank details.
                                            </p>
                                        </div>
                                    </div>
                                    <SlideOutErrors :show="showErrors"/>
                                    <SlideoutFlashMessage ref="create_vat_rates_slideout_flash"/>
                                    <div class="flex-1 flex flex-col justify-between">
                                        <div class="px-4 divide-y divide-gray-200 sm:px-6">
                                            <div class="space-y-6 pt-6 pb-5">
                                                <h2 class="text-xl text-bold">Add New Bank Account</h2>
                                                <input hidden type="text" v-model="form.id">
                                                <div>
                                                    <label for="region-name" class="block text-sm text-gray-900 required">
                                                        Currency
                                                    </label>
                                                    <div class="mt-1">
                                                        <v-select required class="h-full mt-1" :options="currencies" label="name" :reduce="currency => currency.id" v-model="form.currency" placeholder="Please Select" />
                                                    </div>
                                                </div>
                                                <div>
                                                    <label for="vat-rate-percentage" class="block text-sm text-gray-900 required">
                                                        Bank Account
                                                    </label>
                                                    <div class="mt-1">
                                                        <v-select required class="h-full mt-1" :options="enhancedBankDetails" label="label" :reduce="bank_detail => bank_detail.id" v-model="form.bank_details" placeholder="Please Select" />
                                                    </div>
                                                </div>
                                                <div class="flex justify-end">
                                                    <button :disabled="!form.currency || !form.bank_details" type="submit"
                                                            class="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm rounded-md text-white bg-pitch-purple hover:bg-dark-pitch-purple focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:bg-gray-500">
                                                        <span v-if="loading === true">Saving Bank Details..</span>
                                                        <span v-else>Add Bank Account</span>
                                                    </button>
                                                </div>
                                                <h2 class="text-xl text-bold">Bank Accounts</h2>
                                                <div class="shadow overflow-hidden border-b border-gray-200 sm:rounded-lg relative">
                                                    <table class="min-w-full divide-y divide-gray-200">
                                                        <thead class="bg-gray-50">
                                                            <tr>
                                                                <th scope="col"
                                                                    class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                                                    Bank Account
                                                                </th>
                                                                <th scope="col"
                                                                    class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                                                    Currency
                                                                </th>
                                                                <th scope="col" class="relative px-6 py-3">
                                                                    <span class="sr-only">Edit</span>
                                                                </th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            <tr v-if="trading_details.bank_accounts.length > 0" v-for="(bank_account, index) in trading_details.bank_accounts"
                                                                :class="{'bg-gray-100': index % 2}">
                                                                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                                    {{ bank_account.bank_details.account_name }}
                                                                </td>
                                                                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                                    {{ bank_account.currency.name }}
                                                                </td>
                                                                <td class="px-6 w-1/5 py-4 whitespace-nowrap text-right text-sm">
                                                                    <a @click="deleteBankAccount(bank_account)"
                                                                    class="text-red-600 hover:text-red-900 float-right hover:cursor-pointer">
                                                                        <svg xmlns="http://www.w3.org/2000/svg" fill="none"
                                                                            viewBox="0 0 24 24" class="w-4 h-4"
                                                                            stroke="currentColor">
                                                                            <path stroke-linecap="round"
                                                                                stroke-linejoin="round" stroke-width="2"
                                                                                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
                                                                        </svg>
                                                                    </a>
                                                                </td>
                                                            </tr>
                                                            <tr v-else>
                                                                <td colspan="2" class="px-6 py-4 whitespace-nowrap text-sm text-gray-900" :class="{'hidden':loading === true}">
                                                                    No associated bank accounts.
                                                                </td>
                                                            </tr>
                                                        </tbody>
                                                    </table>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div class="flex-shrink-0 px-4 py-4 flex justify-end">
                                    <button type="button" @click="close()"
                                        class="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
                                        Close
                                    </button>
                                </div>
                            </form>
                        </div>
                    </TransitionChild>
                </div>
            </div>
        </Dialog>
    </TransitionRoot>
</template>

